import { Component, Input } from '@angular/core';
import { domainBadgeColorsFromAccentHex, isParaHexLikelyTooLightForStroke, legendChipForeground, normalizeChipColor } from '../../utils/para-color.util';
import type { ParaStudentBadgeMilestoneOverlay } from '../../progress-table/para-milestone.util';

@Component({
	selector: 'app-para-student-badge',
	templateUrl: './student-badge.component.html',
	styleUrls: ['./student-badge.component.css'],
})
export class StudentBadgeComponent {
	/** Student row object (required). */
	@Input() student: any | null = null;

	/** Milestone tier + accent for the icon (falls back to tier 1 + text color when unset). */
	@Input() milestoneOverlay: ParaStudentBadgeMilestoneOverlay | null = null;

	private get highestChallenge(): any | null {
		return this.student?.highestChallenge ?? null;
	}

	get resolvedPhase(): string | number | null {
		return (
			this.highestChallenge?.phase?.shortLabel ??
			(this.student as any)?.nextChallenge?.phase?.shortLabel ??
			(this.student as any)?.currentChallenge?.phase?.shortLabel ??
			'P1'
		);
	}

	get resolvedChallenge(): string | number | null {
		const hc = this.highestChallenge;
		const label = hc?.challengeLabel ?? hc?.challengeDisplay ?? hc?.label ?? null;
		if (label != null && String(label).trim() !== '') return String(label).trim();
		const raw = hc?.challengeId ?? hc?.id ?? null;
		const n = Number(raw);
		if (!Number.isFinite(n)) return null;
		return String(Math.floor(n)).padStart(2, '0');
	}

	get resolvedPhaseChipColor(): string | null {
		const hc = this.highestChallenge;
		return normalizeChipColor(
			hc?.phase?.color ??
				(this.student as any)?.nextChallenge?.phase?.color ??
				(this.student as any)?.currentChallenge?.phase?.color ??
				null
		);
	}

	private get derivedBadgeColors(): { fg: string; border: string } | null {
		const badge = (this.student as any)?.__paraBadge ?? null;
		if (badge?.bg && badge?.fg && badge?.border) {
			return { fg: String(badge.fg), border: String(badge.border) };
		}
		// Normalized PARA contract: use root `nextChallenge/currentChallenge.domain.color`.
		const rawDomainHex =
			(this.student as any)?.nextChallenge?.domain?.color ?? (this.student as any)?.currentChallenge?.domain?.color ?? null;
		const domainHex = normalizeChipColor(rawDomainHex);
		if (domainHex) {
			const b = domainBadgeColorsFromAccentHex(domainHex);
			return { fg: b.fg, border: b.border };
		}

		// Fallback: phase color for stroke accents.
		const chip = this.resolvedPhaseChipColor;
		if (!chip) {
			return { fg: '#0F172A', border: 'rgba(148, 163, 184, 0.9)' };
		}
		return { fg: legendChipForeground(chip), border: chip };
	}

	get resolvedTextColor(): string | null {
		return this.derivedBadgeColors?.fg ?? null;
	}

	get resolvedBorderColor(): string | null {
		return this.derivedBadgeColors?.border ?? null;
	}

	get milestoneOverlayGlyphColor(): string {
		const fg = this.resolvedTextColor ?? '#0F172A';
		const accent = normalizeChipColor(this.milestoneOverlay?.accentHex ?? null);
		if (!accent) {
			return fg;
		}
		// Pastel milestone colors from the API read as “missing art” on domain/phase tints.
		if (!isParaHexLikelyTooLightForStroke(accent, 198)) {
			return accent;
		}
		const border = normalizeChipColor(this.resolvedBorderColor);
		if (border && !isParaHexLikelyTooLightForStroke(border, 215)) {
			return border;
		}
		return fg;
	}

	/** Tier clamped by {@link ParaMilestoneIconComponent}; default 1 when no overlay. */
	get displayMilestoneTier(): number {
		return this.milestoneOverlay?.tier ?? 1;
	}

	get milestoneGlyphSvgClass(): string {
		return 'tw-block tw-h-[92cqi] tw-w-[92cqi] tw-max-h-full tw-max-w-full';
	}

	public isBlankValue(v: unknown): boolean {
		if (v == null) return true;
		const s = String(v).trim();
		// "NA" is a meaningful display value for badges; don't treat it as blank.
		return s === '' || s === '—';
	}

	get ariaLabel(): string {
		const p = !this.isBlankValue(this.resolvedPhase) ? `Phase ${this.resolvedPhase}` : null;
		const c = !this.isBlankValue(this.resolvedChallenge) ? `Challenge ${this.resolvedChallenge}` : null;
		const mo = this.milestoneOverlay;
		const t = this.displayMilestoneTier;
		const tier =
			mo?.tier != null
				? mo.isAspirational
					? `Next milestone tier ${t}`
					: `Highest milestone tier ${t}`
				: `Milestone tier ${t}`;
		return [p, c, tier].filter(Boolean).join(', ');
	}

}
